import { useMemo } from 'react';

import { SearchQuery } from '~/features/advanced-search/types';
import { useCurrentUser } from '~/hooks/useCurrentUser';

import { getReadOnlyFiltersForTab, hasId } from '../TicketFilters.utils';
import { TicketCompletionType } from '../workflowTicketTypes';

/**
 * Hook to merge persisted user filters with tab-specific read-only filters.
 * When there's a conflict (same field ID), read-only filters take precedence.
 *
 * @param persistedFilters - Filters from localStorage (user-selected)
 * @param activeTab - Current active tab
 * @returns Merged query where read-only filters override conflicting persisted filters
 */
export const useMergedFilters = (
  persistedFilters: SearchQuery,
  activeTab: TicketCompletionType,
): SearchQuery => {
  const currentUser = useCurrentUser();

  return useMemo(() => {
    const readOnlyFilters = getReadOnlyFiltersForTab(activeTab, currentUser.id);

    if (readOnlyFilters.length === 0) {
      return persistedFilters;
    }

    const readOnlyFieldIds = new Set(
      readOnlyFilters.filter(hasId).map((q) => q.id),
    );

    const nonConflictingUserFilters = persistedFilters.filter(
      (filter) => !hasId(filter) || !readOnlyFieldIds.has(filter.id),
    );

    return [...nonConflictingUserFilters, ...readOnlyFilters];
  }, [persistedFilters, activeTab, currentUser.id]);
};
